import { action } from "mobx";
import {AppState, LayerFilter, LayerSelection, Mode} from "./app-mobx";
import {MapState} from "../../core/map-state";

/**
 * Encapsulate all functions as an API to configure this map
 */
export class Api {
    appStore: AppState;
    mapStore: MapState;
    handleUnmount: (renderFlag: boolean | void) => void;
    defaultExtent: () => void;

    constructor(store: AppState, mapStore: MapState,
                handleUnmount: (renderFlag: boolean | void) => void, defaultExtent: () => void) {
        this.appStore = store;
        this.mapStore = mapStore;
        this.handleUnmount = handleUnmount;
        this.defaultExtent = defaultExtent;
    }

    /**
     * Set the mode for the application (e.g. "raster" or "sketch")
     *
     * @param mode: The mode as a typed string
     */
    @action setMode(mode: Mode): void {
        this.appStore.setMode(mode);
    }

    /**
     * Set the list of visible layers by layer ids. The ids are build with the metauid of the service object
     *
     * @param layers: A list of layer ids as strings (metauid or title)
     * @param {number} [layerId]: Only allowed if layers is a string.
     */
    @action setVisibleLayers(layers: string | Array<string | LayerSelection>, layerId?: number | number[]): void {
        if (typeof layers === "undefined") {
            layers = [];
        }
        this.appStore.setMapFilters([]);
        this.appStore.setVisibleLayers(layers, layerId);
    }

    /**
     * Set the list of visible background layers by layer ids. The ids are build with the metauid of the service object
     *
     * @param layers: A list of layer ids as strings (metauid or title)
     * @param {number} [layerId]: Only allowed if layers is a string.
     */
    @action setBackgroundLayers(layers: string | Array<string | LayerSelection>, layerId?: number | number[]): void {
        if (typeof layers === "undefined") {
            layers = [];
        }
        this.appStore.setBackgroundLayers(layers, layerId);
    }

    /**
     * Set the list of visible layers by layer ids. The ids are build with the metauid of the service object
     *
     * @param {string} [metauid]: metauid of the layer
     * @param {number} [layerId]: Only allowed if layers is a string.
     * @param {string} [query]: Filter qeury
     */
    @action setLayerFilter(metauid?: string, layerId?: number, query?: string): void {
        const filters: LayerFilter[] = [];
        if (typeof metauid !== "undefined" && typeof layerId !== "undefined" && typeof query !== "undefined") {
            filters.push({metauid, layerId, query});
        }
        this.appStore.setMapFilters(filters);
    }

    /**
     * Set Basemap
     *
     * @param url: The URL to the REST endpoint of the map service.
     * @param {string} [title]: Only allowed if layers is a string.
     */
    @action setBasemap(url: string, title?: string): void {
        this.mapStore.setBasemap(url, title);
    }

    /**
     * Set the current available sketch create tools
     *
     * @param toolNames: A list of create tool names (e.g. ["polygon"]
     */
    @action setSketchCreateTools(toolNames: string[]): void {
        this.appStore.setSketchCreateTools(toolNames);
    }

    /**
     * Set the current available sketch select tools
     *
     * @param toolNames: A list of select tool names (e.g. ["lasso-selection"]
     */
    @action setSketchSelectTools(toolNames: string[]): void {
        this.appStore.setSketchSelectTools(toolNames);
    }
    /**
     * Set limit of the maximum object count
     *
     * @param [count]: if empty it will use 999 as limit
     */
    @action setMaxObject(count?: number): void {
        this.appStore.setMaxObject(count);
    }

    /**
     * Add a handler function listening on sketch actions
     *
     * @param handler: The handler function being assigned to the sketch actions
     */
    @action setSketchHandler(handler: (graphics: string) => void): void {
        this.appStore.setSketchHandler(handler);
    }
    /**
     * Completes the active sketch operation.
     */
    @action completeSketch(): void {
        this.appStore.completeSketch();
    }
    /**
     * Add Esri geometry JSON objects to the map
     */
    @action addGraphics(layerName: string, geometryJson: JSON | JSON[]): void {
        this.appStore.addGraphicsByJson(layerName, geometryJson);
    }
    /**
     * Empty a layer by deleting all graphic objects.
     *
     * @param [layerName]: The layerName on which the graphics should be removed
     */
    @action emptyGraphics(layerName?: string): void {
        this.appStore.emptyGraphics(layerName);
    }
    /**
     * Get all graphic object of a layer, ignore labels if no layerName provided
     *
     * @param [layerName]: The layerName on which the graphics should retrieved
     */
    getGraphics(layerName?: string): string {
        return this.appStore.dumpGraphics(layerName);
    }
    redraw(): void {
        this.handleUnmount();
        this.handleUnmount(true);
    }
    unmount(): void {
        this.handleUnmount();
    }
    mount(): void {
        this.handleUnmount(true);
    }
    defaultExtend(): void {
        this.defaultExtent();
    }
}
